import { Flex, Button, Row, Col, Card, List, Space, Dropdown, Empty, Typography, Modal, notification, Input } from "antd";
import React, { useEffect, useState, useMemo } from "react";
import FormSetup from "../Forms/FormSetup";
import InfiniteScrollList, { InfiniteScrollListProps } from "../Forms/ListView/InfiniteScrollList";
import HeadingSubHeading from "../ViewHeading/HeadingSubHeading";
import { ExclamationCircleOutlined, MoreOutlined, SearchOutlined } from '@ant-design/icons'
import { MasterConfigs } from "../../interfaces/entity";
import { deleteRow } from "../../actions/apiCall";
import { useDispatch } from "react-redux";
import { debounce } from 'lodash';
const { Title, Text } = Typography;

export interface CommonViewProps {
    config: MasterConfigs;
    listProps?: InfiniteScrollListProps;
    componentConfig?: {
        title?: string;
        subTitle?: string;
        cardTitle?: string;
        buttonName?: string;
        emptyDescription?: string;
        renderComponent?: (record: any) => React.ReactNode;
        renderListItem?: (record: any) => React.ReactNode;
        selectedItemId?: (id) => void;
        customHeading?: (item) => React.ReactNode;
    };
}

const CommonView: React.FC<CommonViewProps> = ({
    config,
    componentConfig,
    listProps,
}) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [selectedId, setSelectedId] = React.useState(null);
    const [selectedRecord, setSelectedRecord] = React.useState(null);
    const [load, setLoad] = React.useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const dispatch = useDispatch();

    // Debounce search input
    const debouncedSetSearch = useMemo(
        () => debounce((value: string) => {
            setDebouncedSearch(value);
        }, 300),
        []
    );

    useEffect(() => {
        debouncedSetSearch(searchValue);
        return () => {
            debouncedSetSearch.cancel();
        };
    }, [searchValue, debouncedSetSearch]);

    // Build search query params
    const searchQueryParams = useMemo(() => {
        if (!config.enableListSearch || !debouncedSearch.trim()) {
            return config.queryParams || {};
        }

        const searchKeys = config.listSearchKeys || ['name'];
        const searchQuery: any = { ...config.queryParams };

        // Build search query based on keys
        if (searchKeys.length === 1) {
            // Single key search
            searchQuery[searchKeys[0]] = debouncedSearch.trim();
        } else {
            // Multiple keys - use search parameter if API supports it
            searchQuery.search = debouncedSearch.trim();
            searchQuery.keys = searchKeys.join(',');
        }

        return searchQuery;
    }, [config.enableListSearch, config.listSearchKeys, debouncedSearch, config.queryParams]);

    const onClose = () => {
        setIsOpen(false);
        setLoad(false)
        setSelectedId(null)
    }

    const onDelete = (id) => {
        Modal.confirm({
            title: 'Do you want to delete this record?',
            icon: <ExclamationCircleOutlined />,
            onOk() {
                dispatch(deleteRow(config.api() + '/' + id)).then(res => {
                    console.log("Reeeeeeeee", res)
                    if (res.code == 200) {
                        notification.success({ message: 'Record deleted successfully' })
                        setLoad(true)
                        if (listProps?.reload != undefined) listProps.reload = true
                        setSelectedRecord(null)
                        setSelectedId(null)
                    }
                })
            },
            onCancel() {
                console.log('Cancel');
            },
        });
    }

    useEffect(() => {
        if (selectedId) {
            setIsOpen(true)
        }
    }, [selectedId])

    // config.onRefresh = () => {
    //     setLoad(true)
    //     setSelectedId(null)
    // }

    return (
        <>
            <FormSetup config={config} open={isOpen} onClose={onClose} id={selectedId} />
            <Flex justify="space-between" align="center" style={{ marginBottom: 10 }}>
                {componentConfig?.title && <HeadingSubHeading
                    titleProps={{ level: 5 }}
                    title={componentConfig?.title}
                    subTitle={componentConfig?.subTitle}
                />}
                {componentConfig?.buttonName && <Button type="primary" onClick={() => setIsOpen(true)}>{componentConfig?.buttonName}</Button>}
            </Flex>
            <Row gutter={[16, 16]}>
                <Col span={4}>
                    <Card
                        title={componentConfig?.cardTitle}
                        className="custom-card"
                    >
                        {config.enableListSearch && (
                            <div style={{ marginBottom: 12, padding: '0 4px' }}>
                                <Input
                                    placeholder={config.listSearchPlaceholder || 'Search...'}
                                    prefix={<SearchOutlined />}
                                    value={searchValue}
                                    onChange={(e) => setSearchValue(e.target.value)}
                                    allowClear
                                    style={{ width: '100%' }}
                                />
                            </div>
                        )}
                        <InfiniteScrollList
                            apiUrl={config.api()}
                            apiQuery={searchQueryParams}
                            height={config.enableListSearch ? 550 : 580}
                            itemsPerPage={20}
                            reload={load}
                            onLoaded={() => setLoad(false)}
                            renderItem={(item) => {
                                return <List.Item key={item._id || item.name}
                                    className={(selectedRecord?._id == item?._id ? 'active_list' : '') + " plan-item"}
                                    style={{ paddingLeft: 20 }}
                                    onClick={() => {
                                        if (componentConfig?.selectedItemId) componentConfig?.selectedItemId(item._id)
                                        setSelectedRecord(item)
                                    }}>
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        {componentConfig?.renderListItem && componentConfig?.renderListItem(item)}
                                    </Space>
                                </List.Item >
                            }}
                            {...listProps}
                        />
                    </Card>
                </Col>
                <Col span={20}>
                    <Card>
                        <Space direction="vertical" style={{ width: '100%' }} size={[10, 10]}>
                            {selectedRecord && <Flex justify="space-between">
                                <div>
                                    {componentConfig?.customHeading ? componentConfig?.customHeading(selectedRecord) : <Title level={5}>{selectedRecord?.name}</Title>}
                                </div>
                                <Dropdown menu={{
                                    items: [{
                                        key: '1',
                                        label: 'Edit',
                                    },
                                    {
                                        key: '2',
                                        label: 'Delete',
                                    }
                                    ],
                                    onClick: (e) => {
                                        if (e.key == '2') {
                                            onDelete(selectedRecord?._id)
                                        } else {
                                            setSelectedId(selectedRecord?._id)
                                        }
                                    }
                                }}>
                                    <Button icon={<MoreOutlined />} type="text" />
                                </Dropdown>
                            </Flex>}
                            {selectedRecord && componentConfig?.renderComponent && componentConfig?.renderComponent(selectedRecord)}
                        </Space>
                        {!selectedRecord && componentConfig?.emptyDescription && <Flex justify="center" align="center">
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={componentConfig?.emptyDescription} />
                        </Flex>}
                    </Card>
                </Col>
            </Row>
        </>
    );
}

export default CommonView;